import React, {
  useRef,
  useState,
} from 'react';
import {
  Elements,
  StripeProvider,
} from 'react-stripe-elements';
import {
  Link,
} from 'react-router-dom';
import i18n from 'i18next';
import {
  coreUtils,
} from '@gymcloud/core';

import config from 'web-config';
import {
  SetNamespace,
  NamespaceLogo,
} from 'web-components';
import {
  Flex,
  Text,
  icons,
  Container,
} from 'web-styled';

import gcScreenshot from 'web-assets/images/purchase/default-screenshot-gc.png';

import StepOneForm from './StepOneForm';
import StepTwoForm from './StepTwoForm';
import * as S from './styled';

const isFloat = (n: number) => Number(n) === n && n % 1 !== 0;
const getAmount = (amount: number) => String(isFloat(amount)
  ? amount.toFixed(2)
  : amount).split('.');

type Props = {
  item: any,
  isPlan?: boolean,
  isTest?: boolean,
  namespace: {
    name: string,
    symbol: string,
    screen_app_url: {
      url: string,
    },
  },
  formTitle?: React.ReactNode,
  secondButtonText?: string,
  confirmationTitle?: React.ReactNode,
  onMobileScreenFirst?: boolean,
  buttonText?: string,
  plansOption: any[],
  onChangePlan: { (plan: any): void },
};

const SignUpWithPurchaseForm = ({
  item,
  isPlan,
  isTest,
  formTitle,
  buttonText,
  namespace,
  secondButtonText,
  confirmationTitle,
  onMobileScreenFirst,
  plansOption,
  onChangePlan,
}: Props) => {
  const form = useRef<HTMLFormElement>(null);
  const [step, setStep] = useState('one');
  const [isLoaded, setLoaded] = useState(false);
  const [userData, setUserData] = useState({});

  const title = isTest ? 'Set Up Your Account' : 'Create Your Account';

  return (
    <Container
      column
      spacing={30}
      margin="0 auto 50px auto"
      padding="35px"
    >
      <Flex
        column
        width="100%"
        align="center"
        justify="center"
      >
        <NamespaceLogo />
      </Flex>
      {isLoaded ? (
        <S.FormWrap>
          <Flex
            column
            align="center"
            width="100%"
            padding="0px 20px"
            spacing={20}
          >
            <Text
              size="1.5em"
              color="#121827"
              weight="bold"
              align="center"
              family="avenir"
            >
              {confirmationTitle || 'Thank You for Your Purchase!'}
            </Text>
            <Flex
              width="250px"
              spacing={5}
              column
            >
              <Text family="avenir" color="#121827">
                1. Go to the
                {' '}
                <a href={config.mobile.app_store_url}>App Store</a>
                {' or '}
                <a href={config.mobile.google_play_url}>Google Play</a>
              </Text>
              <Text family="avenir" color="#121827">
                2. Search “gymcloud” and download “Training App (by GymCloud)”
              </Text>
              <Text family="avenir" color="#121827">
                3. Log in with your email + password
              </Text>
              <Text family="avenir" color="#121827">
                4. Your app icon will be updated and you can now access the
                {' '}
                {namespace.name}
                {' '}
                Training App
              </Text>
            </Flex>
            <S.Links>
              <a href={config.mobile.app_store_url}>
                <img
                  width="90.5%"
                  src={icons.appStore}
                  alt=""
                />
              </a>
              <a href={config.mobile.google_play_url}>
                <img
                  width="100%"
                  src={icons.googlePlay}
                  alt=""
                />
              </a>
            </S.Links>
          </Flex>
        </S.FormWrap>
      ) : (
        <S.Wrapper screenfirst={onMobileScreenFirst}>
          <S.FormData>
            <S.AppScreen
              src={namespace.screen_app_url?.url || gcScreenshot}
              alt=""
            />
            <Flex
              column
              align="center"
              width="80%"
            >
              <Text
                size="18px"
                color="#121827"
                align="center"
                weight="bold"
                family="avenir"
              >
                {`${item.name} `}
              </Text>
              <Text
                size="18px"
                color="#121827"
                weight="bold"
                family="avenir"
              >
                {`$${getAmount(item.amount).join('.')}`}
                {item.interval && (
                  <span>
                    /
                    {coreUtils.getIntervalABBR(item.interval)}
                  </span>
                )}
              </Text>
              <Flex margin="5px 0 0 0">
                {item.description && (
                  <Text align="center" whiteSpace="break-spaces">
                    {item.description}
                  </Text>
                )}
              </Flex>
            </Flex>
          </S.FormData>
          <S.FormWrap ref={form}>
            <S.FormInner>
              {{
                one: (
                  <>
                    <S.FormTitle>
                      {formTitle || title}
                      {isPlan && !isTest && (
                        <Text align="center">
                          Enjoy premium content FREE for 7 Days
                        </Text>
                      )}
                    </S.FormTitle>
                    <StepOneForm
                      isTest={isTest}
                      userData={userData}
                      namespace={namespace}
                      buttonText={buttonText}
                      onSubmit={(user) => {
                        if (isTest) {
                          setLoaded(true);
                        } else {
                          setUserData(user);
                          setStep('two');
                        }

                        form.current.scrollIntoView({ behavior: 'smooth' });
                      }}
                    />
                  </>
                ),
                two: (
                  <>
                    <S.FormTitle>
                      <span>
                        Enter Your Payment Information
                      </span>
                      {isPlan && (
                        <Text align="center">
                          Enjoy premium content FREE for 7 Days
                        </Text>
                      )}
                    </S.FormTitle>
                    <StripeProvider
                      apiKey={config.stripe.key}
                    >
                      <Elements>
                        <StepTwoForm
                          id={item.id}
                          isPlan={isPlan}
                          secondButtonText={secondButtonText}
                          userData={userData}
                          plansOption={plansOption}
                          onSuccess={() => setLoaded(true)}
                          onChangePlan={onChangePlan}
                        />
                      </Elements>
                    </StripeProvider>
                  </>
                ),
              }[step]}
              <Flex
                spacing={3}
                width="100%"
                justify="center"
                align="center"
                padding="10px"
              >
                <Text color="#888" size="14px">
                  Already have an account?
                </Text>
                <Link to="/login">Sign in</Link>
              </Flex>

              {process.env.TARGET_ENV !== 'production' && (
                <Flex
                  width="100%"
                  margin="-10px 0px 10px 0px"
                  justify="center"
                >
                  <SetNamespace />
                </Flex>
              )}
            </S.FormInner>
            {isPlan && (
              <S.Statement>
                {i18n.t('subscription.renewal_text')}
              </S.Statement>
            )}
          </S.FormWrap>
        </S.Wrapper>
      )}
    </Container>
  );
};

SignUpWithPurchaseForm.defaultProps = {
  isPlan: false,
  isTest: false,
  formTitle: null,
  confirmationTitle: null,
  onMobileScreenFirst: true,
  buttonText: 'Create My Account',
  secondButtonText: 'Complete Purchase',
};

export default SignUpWithPurchaseForm;
